import { useState, useRef, CSSProperties } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Building2, Users as UsersIcon, Lock, ShieldCheck, Tag, AlertTriangle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useBillingConfig, PlanTierId, volumeDiscountFor, EffectiveBillingConfig } from '../contexts/BillingConfigContext.tsx';
import { api } from '../api/client.ts';
import Toast, { useToast } from '../components/Toast.tsx';
import SlidingToggle from '../components/SlidingToggle.tsx';
import DarkSelect from '../components/DarkSelect.tsx';
import '../styles/buttonAnimations.css';
import './Billing.css';

// ─── Billing & Plan ───────────────────────────────────────────────────────────
// Pricing/discount/mode config is server-backed (migration_v134/v135,
// server/src/routes/billing.ts) as of the per-org overrides + discount codes work -- this page
// fetches the CURRENT user's own org's effective config (their org's override if one exists,
// otherwise the global default, with any redeemed discount code already applied) over
// GET /api/billing/effective, instead of reading local browser state. That's what makes per-org
// pricing actually visible to that org's own users, not just visible in the admin's own browser.
// No Stripe / real payment processing anywhere -- selecting a tier or redeeming a code changes
// this org's stored config, nothing is charged.
//
// selectedTier/billingCycle (from BillingConfigContext) stay genuinely local/per-viewer: which
// tier's comparison card you're previewing and which cycle you're looking at, not real
// subscription state -- there's no Stripe/payment processing in this app for either to represent.

const TIER_ORDER: PlanTierId[] = ['standard', 'pro', 'enterprise'];
const TIER_LABEL: Record<PlanTierId, string> = { standard: 'Standard', pro: 'Pro', enterprise: 'Enterprise' };
const TIER_ACCENT: Record<PlanTierId, string> = { standard: '#9ca3af', pro: '#f59e0b', enterprise: '#8b5cf6' };
// Selected-state color per tier -- separate from TIER_ACCENT above because Standard's selected
// ring/tint stays the app's blue accent (not its own gray identity color, which would look like an
// unstyled/disabled state), while Pro and Enterprise selected DO use their own identity color.
// Same hex Pro/Enterprise already use for their badge (TIER_ACCENT) and, for Pro, its permanent
// glow border below -- one color per tier, reused everywhere instead of three separate values.
const TIER_SELECT_COLOR: Record<PlanTierId, string> = { standard: '#3b82f6', pro: '#f59e0b', enterprise: '#8b5cf6' };
// Same three colors as TIER_SELECT_COLOR above, pre-mixed to a translucent rgba for the
// billingCardGlow keyframe's soft bloom layer (Billing.css) -- kept as a plain rgba string rather
// than parsing the hex + alpha at runtime, since it's only ever these three known values.
const TIER_SELECT_GLOW: Record<PlanTierId, string> = {
  standard: 'rgba(59,130,246,0.35)', pro: 'rgba(245,158,11,0.35)', enterprise: 'rgba(139,92,246,0.35)',
};
const TIER_FEATURES: Record<PlanTierId, string[]> = {
  standard: [
    'Manual entry only — no AI features',
    'Basic approval email',
    'Basic vendor cards',
    'Manual compliance tracking',
    'Facility Keys — table view only',
  ],
  pro: [
    'Everything in Standard',
    'AI proposal extraction (9/month per facility)',
    'AI compliance analysis',
    'Dashboard customization',
    'Full vendor management — sharing, contracts, spend, ratings',
    'Facility Keys — Board (pegboard) view',
    'SMS notifications',
  ],
  enterprise: [
    'Everything in Pro',
    'Unlimited AI usage',
    'AI chat assistant (Inventory + Learning Hub)',
    'AI vendor & contract comparison',
    'Project bid analysis',
    'Facility Keys — history log, QR codes, AI security audit',
    'Extinguisher data import/export',
  ],
};

// Was never actually used to render a real total -- see the loading-gate below, which now holds
// off rendering ANY cost number until every input has resolved -- but kept as the pre-load `remote`
// fallback so nothing downstream needs an `undefined` check. Values corrected to match the real
// server defaults (server/src/db/migration_v134.sql / billing_config_defaults) -- this previously
// said standard: 49 / pro: 99 / volumeDiscount 10/20, none of which match the real 59 / 109 / 5/10
// this org has been running for a while now. That mismatch was harmless as long as this object was
// only ever transiently visible, but it was ALSO the literal source of the "$49" flash Chris saw --
// while /billing/effective hadn't resolved yet, `remote` WAS this placeholder, and briefly got
// rendered as if it were real data. The loading gate now prevents that regardless of these values
// ever drifting from reality again, but there's no reason to leave them wrong either.
const LOADING_CONFIG: EffectiveBillingConfig = {
  tiers: {
    standard:   { basePricePerFacilityMonth: 59,  seatsIncludedPerFacility: 5,  pricePerExtraSeatMonth: 6 },
    pro:        { basePricePerFacilityMonth: 109, seatsIncludedPerFacility: 10, pricePerExtraSeatMonth: 9 },
    enterprise: { basePricePerFacilityMonth: 159, seatsIncludedPerFacility: 20, pricePerExtraSeatMonth: 14 },
  },
  volumeDiscount10to20: 5,
  volumeDiscount20plus: 10,
  annualDiscount: 15,
  billingMode: 'centralized',
  isOverride: false,
  activeDiscountCode: null,
  orgFacilityCount: 1,
};

// GET /billing/effective now also reports whether the org already has a live Stripe subscription
// (server/src/routes/billing.ts). Extended here rather than in BillingConfigContext.tsx's shared
// EffectiveBillingConfig, which Settings.tsx also consumes and has no use for these.
type FacilityBillingStatus = {
  facilityId: number;
  name: string;
  hasActive: boolean;
  status: string | null;
  planTier: string | null;
  currentPeriodEnd: string | null;
  stripeSubscriptionId: string | null;
};
type BillingEffective = EffectiveBillingConfig & {
  // Centralized mode: the ORG's one subscription.
  hasActiveStripeSubscription?: boolean;
  stripeSubscriptionStatus?: string | null;
  // Per-Facility mode: each subscribed facility's own subscription, keyed by facility id. A
  // facility with no entry has never subscribed. A manager only receives their own facility.
  facilityBilling?: Record<string, FacilityBillingStatus>;
  // Every facility in the org with a live subscription, org_admin/admin only, computed regardless
  // of the org's CURRENT billing_mode -- this is what names the facility blocking a mode switch
  // instead of leaving it as a bare count (empty for a manager, or under a role that can't see it).
  activeFacilitySubscriptions?: FacilityBillingStatus[];
  // Whether the viewer (org_admin/admin) can cancel the ORG-WIDE subscription -- independent of
  // the current billing_mode, same reasoning as server/src/routes/billing.ts's cancel-subscription.
  canManageOrgSubscription?: boolean;
};

// GET /billing/refund-preview's shape -- admin only. amountCents is the SAME linear unused-time
// proration the routine cancel's credit would use, just computed for a real refund instead (see
// server/src/routes/billing.ts). Always recomputed server-side at execute time too -- this is only
// ever a preview for the confirm dialog, never trusted as the actual charge amount.
interface RefundPreviewData {
  amountCents: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  card: { brand: string; last4: string } | null;
  label: string;
}

interface UserRow { id: number; facility_id: number | null; org_id?: number | null; role?: string; }
interface FacilityRow { id: number; name: string; org_id: number | null; }

// ─── Role x billing-mode resolution — THE ONE PLACE this is decided ──────────────
// Everything on this page (snapshot cards, price cards, breakdown, plan buttons, discount-code
// section, restricted page) reads off the single BillingView this returns; nothing else in the
// file may branch on role or billingMode. Truth table (final spec from Chris):
//
//   mode          role        viewMode    canManage  facility picker
//   per_facility  manager     scoped      yes        no  (implicitly their own facility)
//   per_facility  org_admin   scoped      no         yes (view-only)
//   per_facility  admin       scoped      yes        yes (ASSUMPTION: admin bypasses role limits)
//   centralized   org_admin   org-wide    yes        no
//   centralized   admin       org-wide    yes        no  (ASSUMPTION: same as org_admin)
//   centralized   manager     restricted  no         no  (whole page blocked, not read-only)
//
// 'scoped'   = ONE facility's price (tier base − org-wide volume discount [− annual discount]).
// 'org-wide' = that same per-facility rate × the org's total facility count.
// The volume-discount BRACKET is always the org's total facility count in both -- see volumePct.
// Anything not listed (e.g. a plain 'user'/'viewer' who somehow reaches this page) resolves to
// 'restricted' rather than falling through to a price view.
type BillingViewMode = 'scoped' | 'org-wide' | 'restricted';
interface BillingView {
  viewMode: BillingViewMode;
  canManage: boolean;
  showFacilityPicker: boolean;
}
function resolveBillingView(role: string | undefined, billingMode: EffectiveBillingConfig['billingMode']): BillingView {
  if (billingMode === 'per_facility') {
    if (role === 'manager') return { viewMode: 'scoped', canManage: true, showFacilityPicker: false };
    if (role === 'org_admin') return { viewMode: 'scoped', canManage: false, showFacilityPicker: true };
    if (role === 'admin') return { viewMode: 'scoped', canManage: true, showFacilityPicker: true };
  } else {
    if (role === 'org_admin' || role === 'admin') return { viewMode: 'org-wide', canManage: true, showFacilityPicker: false };
  }
  return { viewMode: 'restricted', canManage: false, showFacilityPicker: false };
}

function formatMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
// Whole dollars hide cents ($1,657 vs the real $1,656.80) -- the checkout confirmation must state
// exactly what will be charged.
function formatMoneyExact(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Keyed on the rounded display value so a change (cycle toggle, facility/user count, tier pick)
// remounts the span and replays the CSS keyframe — the "animated number-swap" from the reference
// mockup, ported as a plain CSS animation instead of a JS tween library.
function AnimatedPrice({ value, size = 30 }: { value: number; size?: number }) {
  const rounded = Math.round(value);
  return (
    <span key={rounded} className="billing-price-swap" style={{ fontSize: size, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
      {formatMoney(rounded)}
    </span>
  );
}

function DiscountChip({ label, pct, tone }: { label: string; pct: number; tone: 'blue' | 'green' }) {
  if (pct <= 0) return null;
  const colors = tone === 'blue'
    ? { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6' }
    : { bg: 'rgba(16,185,129,0.15)', fg: '#10b981' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      background: colors.bg, color: colors.fg, whiteSpace: 'nowrap',
    }}>
      {label} −{pct}%
    </span>
  );
}

// Custom dark confirm modal -- CLAUDE_RULES.md: never window.confirm(). Same createPortal +
// modal-overlay/modal + onMouseDown/onMouseUp-outside-close pattern as Tasks.tsx's
// DeleteConfirmModal (the app's own reference for a small confirm dialog), reused rather than
// invented. Cancellation is the one truly destructive action on this page (unlike redeeming a code
// or picking a tier), so it gets a real confirm step -- and explicitly states the credit-not-refund
// behavior right in the dialog, not just in a code comment, since it's a financial detail the person
// clicking the button should see before they click it.
function CancelSubscriptionModal({ label, onConfirm, onClose, canceling }: {
  label: string; onConfirm: () => void; onClose: () => void; canceling: boolean;
}) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (!canceling && mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: '100%' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '1.0625rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <XCircle size={18} style={{ color: '#ef4444' }} /> Cancel {label}?
        </h3>
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 12px', fontSize: '0.9375rem' }}>
          This ends the subscription immediately through Stripe. Access for this plan will stop as soon as it's confirmed.
        </p>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 24px', fontSize: '0.8125rem', lineHeight: 1.5 }}>
          The unused time left in the current period becomes an account <strong>credit</strong> toward a future
          invoice for the same subscription — it is <strong>not</strong> refunded to the card on file.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose} disabled={canceling}>Never mind</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={canceling}>{canceling ? 'Canceling…' : 'Cancel Subscription'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Admin-only real-refund confirm -- deliberately its own component/modal, never the same click as
// CancelSubscriptionModal above. Two states: loading the preview (a live Stripe read, GET
// /refund-preview -- the amount isn't known until this resolves) and showing it. States plainly,
// before the one enabled button, the exact amount and which card it's going to -- the confirm rule
// this ticket asked for -- rather than a generic "issue a refund?" prompt.
function RefundConfirmModal({ label, preview, loading, refunding, onConfirm, onClose }: {
  label: string; preview: RefundPreviewData | null; loading: boolean; refunding: boolean;
  onConfirm: () => void; onClose: () => void;
}) {
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const busy = loading || refunding;
  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (!busy && mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1.0625rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <XCircle size={18} style={{ color: '#ef4444' }} /> Cancel &amp; Refund {label}?
        </h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, background: 'rgba(239,68,68,0.15)', color: '#ef4444', margin: '4px 0 12px',
        }}>
          Admin action — real money
        </span>
        {loading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0 0 24px' }}>Calculating the refund amount from Stripe…</p>
        ) : preview ? (
          <>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 8px', fontSize: '0.9375rem' }}>
              This cancels the subscription immediately (no proration credit -- see the routine Cancel button for that) and sends a REAL refund:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', margin: '0 0 24px' }}>
              <div style={{ fontSize: '1.375rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                {formatMoneyExact(preview.amountCents / 100)}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                {preview.amountCents <= 0
                  ? 'Nothing to refund — no unused time remains in the current billing period.'
                  : preview.card
                    ? <>back to the <strong style={{ color: 'var(--text-secondary)' }}>{preview.card.brand.toUpperCase()} card ending {preview.card.last4}</strong> on file</>
                    : 'back to the card on file (details unavailable)'}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                For the unused time remaining in the current billing period (through {new Date(preview.periodEnd).toLocaleDateString()}).
              </div>
            </div>
          </>
        ) : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose} disabled={busy}>Never mind</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={busy || !preview}>
            {refunding ? 'Processing…' : loading ? 'Loading…' : 'Cancel & Refund'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Shown in place of the whole billingMode/pricing section while dataReady is false (see
// BillingPage) -- simple pulsing bars, no real numbers of any kind, so there is nothing here that
// could ever be mistaken for a real (and possibly wrong) total.
function BillingLoadingSkeleton() {
  const bar = (width: string, height = 16) => (
    <div style={{ width, height, borderRadius: 6, background: 'rgba(255,255,255,0.06)' }} className="billing-skeleton-bar" />
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card billing-card" style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {bar('220px')}
        {bar('380px', 12)}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="card billing-card" style={{ padding: '12px 18px', flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bar('100px', 12)}
            {bar('60px', 20)}
          </div>
        ))}
      </div>
      <div className="card billing-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bar('160px', 12)}
        {bar('90px', 32)}
        {bar('220px', 12)}
      </div>
    </div>
  );
}

export default function BillingPage() {
  const { user, isLoading: authLoading } = useAuth();
  const cfg = useBillingConfig();
  const qc = useQueryClient();
  const { toast, showToast } = useToast();

  const { data: effectiveRes } = useQuery({
    queryKey: ['billing-effective'],
    queryFn: () => api.get<EffectiveBillingConfig>('/billing/effective'),
  });
  const remote: BillingEffective = (effectiveRes as unknown as { data: BillingEffective })?.data ?? LOADING_CONFIG;

  // Root cause of the ORG-WIDE facility count itself being wrong for a manager (a real bug, not
  // just the loading race below): this page used to source facilityCount from GET /api/facilities
  // -- but that endpoint is deliberately role-scoped (facilities.ts), returning just a manager's
  // OWN facility (1), not the org's real total. Silently zeroed out the volume-discount bracket
  // for every manager, since 1 facility never reaches the 11-facility floor. orgFacilityCount now
  // comes from GET /billing/effective instead (server/src/routes/billing.ts's getOrgEffective,
  // queried off the user's own orgId, same for every role) -- also removes a whole separate query
  // (and its own independent resolve timing) from this page, one less moving part in the render
  // race described below.
  const facilityCount = Math.max(1, remote.orgFacilityCount ?? 1);

  const { data: usersRes } = useQuery({
    queryKey: ['billing-user-count'],
    queryFn: () => api.get<UserRow[]>('/users'),
  });
  // GET /users is role-scoped server-side: org_admin/manager get their org's non-admin users, but
  // platform admin gets EVERY org's users plus admin accounts. Narrowed to the org whose config
  // /billing/effective returned (same org + non-admin filter the server applies for everyone
  // else) so seat counts -- and therefore any seat overage -- are identical across roles.
  const allUsers = ((usersRes as unknown as { data: UserRow[] })?.data ?? [])
    .filter(u => u.role !== 'admin' && (u.org_id == null || u.org_id === user?.orgId));
  const userCount = Math.max(1, allUsers.length || 1);

  // Everything the actual cost math depends on: useAuth's user (role/facilityId -- null until
  // /auth/me resolves), billing-effective (billingMode/prices/orgFacilityCount), and the users
  // list (for both org-wide userCount and, below, this one facility's own headcount). Previously
  // rendered progressively as each of these (plus a since-removed /facilities call) resolved
  // independently and in whatever order the network returned them -- an intermediate render using
  // some real data and some still-defaulted data could land on a genuinely different number than
  // the final one, which is exactly the "flashes correct, then reverts" bug: nothing here was
  // actually reverting a correct value on purpose, a partially-loaded render was just being shown
  // as if it were final. Gating the whole cost UI on this instead of letting it render from
  // whatever's available at each intermediate step -- see `dataReady` below.
  // The facility picker's list (org_admin/admin under Per-Facility mode only -- the same
  // GET /facilities the Sidebar switcher uses, which is role-scoped server-side: org_admin gets
  // their org's facilities, admin gets every org's, so it's narrowed to the user's own org here
  // to match the org whose billing config /billing/effective returned). Declared unconditionally
  // (rules of hooks) and `enabled` only once we know a picker is needed.
  const view = resolveBillingView(user?.role, remote.billingMode);
  const { data: facilitiesRes } = useQuery({
    queryKey: ['billing-facilities'],
    queryFn: () => api.get<FacilityRow[]>('/facilities'),
    enabled: view.showFacilityPicker,
  });
  const orgFacilities = ((facilitiesRes as unknown as { data: FacilityRow[] })?.data ?? [])
    .filter(f => !f.org_id || f.org_id === user?.orgId);

  // Which ONE facility a 'scoped' view prices. Manager: implicitly their own. Picker roles: the
  // one chosen in the picker (local to this page -- deliberately NOT the global FacilityContext
  // switcher, which would change every other page's data), defaulting to their own facility if it
  // is in the list, else the first one.
  const [pickedFacilityId, setPickedFacilityId] = useState<number | null>(null);
  const scopedFacility: { id: number | null; name: string } = view.showFacilityPicker
    ? (() => {
        const chosen = orgFacilities.find(f => f.id === pickedFacilityId)
          ?? orgFacilities.find(f => f.id === user?.facilityId)
          ?? orgFacilities[0];
        return { id: chosen?.id ?? null, name: chosen?.name ?? '' };
      })()
    : { id: user?.facilityId ?? null, name: user?.facilityName ?? 'your facility' };

  const dataReady = !authLoading && !!user && !!effectiveRes && !!usersRes
    && (!view.showFacilityPicker || !!facilitiesRes);

  const { canManage } = view;
  const isScoped = view.viewMode === 'scoped';
  const scopedUserCount = Math.max(1, allUsers.filter(u => u.facility_id === scopedFacility.id).length || 1);
  const billableFacilityCount = isScoped ? 1 : facilityCount;
  const billableUserCount = isScoped ? scopedUserCount : userCount;

  // JUDGMENT CALL (flagged per instruction, not silently picked): volumePct is deliberately still
  // computed from the org-wide `facilityCount`, never `billableFacilityCount` -- even in the
  // scoped single-facility view. Reasoning: volume discount brackets are described everywhere else
  // in this codebase (the snapshot chip below, DiscountChip, the "at 11-20 facilities" copy) as a
  // benefit of the ORG's total scale, not something a single facility earns on its own -- the real
  // per-facility rate on the org's actual invoice already reflects whatever bracket the org's full
  // facility count qualifies for, so showing this one facility's slice WITHOUT that earned discount
  // would overstate what it actually costs today. The alternative reading (volume discount is a
  // Centralized-view-only concept, zeroed out per-facility) is real and defensible too -- landed on
  // keeping it because "org-wide" is stated as the basis for the bracket, never as a Centralized-
  // mode-only concept, everywhere else this codebase talks about it. Confirmed against real
  // numbers now that orgFacilityCount is actually correct for a manager too (previously silently
  // 1, always 0%): Buckingham (org has 16 facilities -> 5% bracket) on Standard comes to $56.05/mo
  // with this discount applied (59 * 0.95, 5 of 5 included seats in use, no overage) -- this is
  // the number that should now actually render, confirming the earlier judgment call's intended
  // $56.05 (not the $59.00 flat alternative) now that the bracket calc isn't silently broken.
  const volumePct = volumeDiscountFor(facilityCount, remote);
  const annualPct = cfg.billingCycle === 'yearly' ? remote.annualDiscount : 0;

  // Facility/user counts default to the VIEW's (scoped to one facility, or org-wide); the checkout
  // confirmation below passes the org-wide counts explicitly, since Stripe always bills the whole org.
  function tierCost(tierId: PlanTierId, facilities = billableFacilityCount, users = billableUserCount) {
    const t = remote.tiers[tierId];
    const perFacilityRate = t.basePricePerFacilityMonth * (1 - volumePct / 100) * (1 - annualPct / 100);
    const facilitiesSubtotal = perFacilityRate * facilities;
    const includedSeats = t.seatsIncludedPerFacility * facilities;
    const extraSeats = Math.max(0, users - includedSeats);
    const seatOverage = extraSeats * t.pricePerExtraSeatMonth;
    const totalMonthly = facilitiesSubtotal + seatOverage;
    return { perFacilityRate, facilitiesSubtotal, includedSeats, extraSeats, seatOverage, totalMonthly };
  }

  const selectedCost = tierCost(cfg.selectedTier);
  // What Stripe will actually charge, same formula/counts as the server's create-checkout-session:
  // the scoped single-facility figure under Per-Facility mode (== selectedCost, the view's own
  // counts), the org-wide figure under Centralized mode.
  const chargeCost = isScoped ? selectedCost : tierCost(cfg.selectedTier, facilityCount, userCount);

  // ── Discount code redemption ──────────────────────────────────────────────────
  // ── Stripe Checkout ───────────────────────────────────────────────────────────
  // "Select This Plan" on the tier cards only PREVIEWS a tier (cfg.setSelectedTier, local state).
  // The one thing that starts checkout is the explicit "Proceed to Checkout" button in the Cost
  // breakdown card, for cfg.selectedTier / cfg.billingCycle. First subscription only: with a live
  // subscription that button is replaced by a note (plan changes belong to the Customer Portal).
  // Whose subscription is being looked at follows the same scope as the prices: under Per-Facility
  // mode the scoped facility's own entry (one facility's subscription never affects another's),
  // under Centralized mode the org's.
  const facilitySub = isScoped && scopedFacility.id != null ? remote.facilityBilling?.[String(scopedFacility.id)] : undefined;
  const subscriptionActive = isScoped ? !!facilitySub?.hasActive : !!remote.hasActiveStripeSubscription;
  const planLocked = canManage && subscriptionActive;
  const facilitySubTier = facilitySub?.planTier && TIER_LABEL[facilitySub.planTier as PlanTierId] ? ` ${TIER_LABEL[facilitySub.planTier as PlanTierId]}` : '';
  const subscriptionSummary = !subscriptionActive
    ? null
    : isScoped
      ? `${scopedFacility.name || 'This facility'} has an active${facilitySubTier} subscription${facilitySub?.status ? ` (${facilitySub.status})` : ''}.`
      : 'This organization has an active subscription.';
  const [startingCheckout, setStartingCheckout] = useState(false);
  async function handleCheckout() {
    if (!canManage || planLocked || startingCheckout) return;
    setStartingCheckout(true);
    try {
      // api.post already unwraps the axios response to the JSON body (client.ts `.then(r => r.data)`),
      // and this endpoint deliberately returns { success, url } at the top level -- not the usual
      // { success, data } wrapper -- so the url is `res.url`, not `res.data.url`.
      // facilityId only matters for a scoped (Per-Facility) charge; the server ignores it for a
      // manager (always their own facility) and uses it for admin, who picks one in the picker.
      const res = await api.post('/billing/create-checkout-session', {
        tierId: cfg.selectedTier,
        billingCycle: cfg.billingCycle,
        ...(isScoped && scopedFacility.id != null ? { facilityId: scopedFacility.id } : {}),
      });
      const url = (res as unknown as { url?: string | null }).url;
      if (url) {
        window.location.href = url; // leaving the page -- leave the button disabled
        return;
      }
      showToast('Could not start checkout', true);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not start checkout';
      showToast(message, true);
    }
    setStartingCheckout(false);
  }

  // ── Cancel Subscription ────────────────────────────────────────────────────────
  // Minimum viable cancel action (not the Customer Portal -- Phase 4, separate): one server-
  // validated action reachable from this page instead of requiring Stripe dashboard access.
  // `cancelTarget` is either the currently-viewed subscription (from the Cost breakdown card's
  // planLocked note) or one row of the conflicts notice below -- same modal, same handler either
  // way. Optimistic UI: api.post's response already carries Stripe's own synchronous
  // { status: 'canceled' } (see server/src/routes/billing.ts), so the confirm toast doesn't wait on
  // the webhook -- but the actual hasActive/locked state shown on screen comes from the next
  // /billing/effective refetch below, which lands moments later once the webhook writes it (a plain
  // invalidate can briefly still show the old "active" state -- acceptable, matches "reflected
  // immediately via the existing webhook flow" rather than this endpoint duplicating that write).
  const [cancelTarget, setCancelTarget] = useState<{ facilityId?: number; label: string } | null>(null);
  const [canceling, setCanceling] = useState(false);
  async function handleCancelConfirm() {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      await api.post('/billing/cancel-subscription', cancelTarget.facilityId != null ? { facilityId: cancelTarget.facilityId } : {});
      showToast(`${cancelTarget.label} canceled`);
      setCancelTarget(null);
      qc.invalidateQueries({ queryKey: ['billing-effective'] });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not cancel the subscription';
      showToast(message, true);
    } finally {
      setCanceling(false);
    }
  }

  // ── Real refund (admin only) ─────────────────────────────────────────────────
  // A DELIBERATELY separate action from the routine (credit) Cancel above -- never reachable by
  // the same click, never shown to org_admin/manager. See server/src/routes/billing.ts's own
  // header comment on GET /refund-preview + POST /cancel-and-refund for the full design writeup
  // (calculation method, why admin-only, ordering on partial failure).
  const [refundTarget, setRefundTarget] = useState<{ facilityId?: number; label: string } | null>(null);
  const [refundPreview, setRefundPreview] = useState<RefundPreviewData | null>(null);
  const [loadingRefundPreview, setLoadingRefundPreview] = useState(false);
  const [refunding, setRefunding] = useState(false);
  async function openRefundFlow(target: { facilityId?: number; label: string }) {
    setRefundTarget(target);
    setRefundPreview(null);
    setLoadingRefundPreview(true);
    try {
      const res = await api.get<RefundPreviewData>('/billing/refund-preview', target.facilityId != null ? { facilityId: target.facilityId } : undefined);
      setRefundPreview((res as unknown as { data: RefundPreviewData }).data);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not compute a refund preview';
      showToast(message, true);
      setRefundTarget(null);
    } finally {
      setLoadingRefundPreview(false);
    }
  }
  async function handleRefundConfirm() {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const res = await api.post('/billing/cancel-and-refund', refundTarget.facilityId != null ? { facilityId: refundTarget.facilityId } : {});
      const data = (res as unknown as { data: { refunded: boolean; refundedAmountCents: number; refundError?: string; reason?: string } }).data;
      if (data.refunded) {
        showToast(`${refundTarget.label} canceled — ${formatMoneyExact(data.refundedAmountCents / 100)} refunded`);
      } else if (data.refundError) {
        showToast(data.refundError, true);
      } else {
        showToast(`${refundTarget.label} canceled (${data.reason ?? 'nothing to refund'})`);
      }
      setRefundTarget(null);
      qc.invalidateQueries({ queryKey: ['billing-effective'] });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not cancel and refund';
      showToast(message, true);
    } finally {
      setRefunding(false);
    }
  }

  const [codeInput, setCodeInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  async function handleRedeem() {
    if (!codeInput.trim()) return;
    setRedeeming(true);
    try {
      await api.post('/billing/redeem', { code: codeInput.trim() });
      showToast('Discount code applied');
      setCodeInput('');
      qc.invalidateQueries({ queryKey: ['billing-effective'] });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not apply that code';
      showToast(message, true);
    } finally {
      setRedeeming(false);
    }
  }

  return (
    <div className="billing-btn-pulse-scope">
      <Toast toast={toast} />
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing &amp; Plan</h1>
          <p className="page-subtitle">
            Prototype — pricing is real per-org config, but no card is charged (no Stripe/payment processing).
          </p>
        </div>
      </div>

      {/* Everything below depends on remote/user/allUsers -- gated on dataReady (defined above)
          instead of rendering progressively off whatever's resolved so far. This is what actually
          fixes the "flashes correct, then reverts" bug: it was never a value un-fixing itself,
          it was an in-flux intermediate render (some real data, some still-defaulted) being shown
          as if it were the final one. Nothing past this point can render off a partial state
          anymore -- either everything needed has loaded, or none of this renders yet. */}
      {!dataReady ? (
        <BillingLoadingSkeleton />
      ) : view.viewMode === 'restricted' ? (
        // Centralized mode + manager (or any role not in the truth table): the whole page is
        // blocked -- no pricing, no plan cards, no discount-code box is rendered at all, rather
        // than a read-only copy of them. There is no existing rendered "no access" component in
        // this app (ProtectedRoute only <Navigate>s to /dashboard, and it can't see billingMode,
        // which is only known here after /billing/effective resolves), so this reuses the same
        // .card + Lock icon + muted-text pattern the rest of the app uses for gated content.
        <div className="card billing-card" style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
          <Lock size={28} color="var(--text-muted)" />
          <h3 style={{ margin: 0 }}>Restricted Access</h3>
          <p style={{ margin: 0, maxWidth: 420, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            This organization&rsquo;s billing is managed centrally by your Org Admin. You don&rsquo;t have access to the Billing &amp; Plan page.
          </p>
        </div>
      ) : (
      <>
      {/* Billing Mode readout — the mode itself is only editable at Settings -> Development ->
          Billing Configuration (admin-only); this page just reflects who can currently manage. */}
      <div className="card billing-card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 20px' }}>
        <ShieldCheck size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Billing Mode: {remote.billingMode === 'centralized' ? 'Centralized' : 'Per-Facility'}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {isScoped
              ? (view.showFacilityPicker
                  ? `Each facility is priced on its own -- pick a facility below to see its rate. ${canManage ? '' : 'Org Admin can view it read-only; the facility’s Manager manages its plan.'}`
                  : 'You manage your own facility’s plan. The price below is for your facility only, not the org-wide total.')
              : 'Org Admin manages this subscription for the whole organization; the total below covers every facility.'}
            {' '}Changed at Settings → Development → Billing Configuration.
          </div>
        </div>
        {!canManage && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
            background: 'rgba(156,163,175,0.15)', color: '#9ca3af', flexShrink: 0,
          }}>
            <Lock size={11} /> Read-only for you
          </span>
        )}
      </div>

      {/* Billing conflicts notice — org_admin/admin only (remote.canManageOrgSubscription; a manager
          can't act on another facility or the org-wide sub, and never reaches this page at all
          under Centralized mode -- see resolveBillingView's 'restricted'). PERSISTENT, not a toast:
          visible the moment /billing/effective loads, not only after a blocked checkout attempt.
          Computed regardless of the CURRENT billing_mode -- names every facility actually blocking
          things, with a real Cancel action per row, instead of the old bare count in the checkout
          409. Two independent conditions, either or both can show at once (a genuinely stale state,
          but not one this notice should hide). */}
      {remote.canManageOrgSubscription && (remote.activeFacilitySubscriptions?.length ?? 0) > 0 && remote.billingMode === 'centralized' && (
        <div className="card billing-card" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, padding: '14px 20px', border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>
                {remote.activeFacilitySubscriptions!.length === 1 ? '1 facility still has' : `${remote.activeFacilitySubscriptions!.length} facilities still have`} its own active subscription{remote.activeFacilitySubscriptions!.length === 1 ? '' : 's'}
              </strong>{' '}despite Centralized billing mode. Cancel {remote.activeFacilitySubscriptions!.length === 1 ? 'it' : 'them'} below before (or while) running the org-wide subscription, or these facilities will be billed twice.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {remote.activeFacilitySubscriptions!.map(f => (
              <div key={f.facilityId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.03)' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                  {f.name}
                  {f.planTier && TIER_LABEL[f.planTier as PlanTierId] && <span style={{ color: 'var(--text-muted)' }}> — {TIER_LABEL[f.planTier as PlanTierId]}{f.status ? ` (${f.status})` : ''}</span>}
                </span>
                <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444', fontSize: 12 }} onClick={() => setCancelTarget({ facilityId: f.facilityId, label: `${f.name}'s subscription` })}>
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {remote.canManageOrgSubscription && remote.billingMode === 'per_facility' && remote.hasActiveStripeSubscription && (
        <div className="card billing-card" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, padding: '14px 20px', border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.06)' }}>
          <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>This organization still has an active org-wide subscription</strong> despite Per-Facility billing mode.
            It blocks any facility from starting its own checkout until it's canceled.
          </div>
          <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444', fontSize: 12, flexShrink: 0 }} onClick={() => setCancelTarget({ label: 'the org-wide subscription' })}>
            Cancel
          </button>
        </div>
      )}

      {/* Facility picker -- org_admin (view-only) / admin (full manage) under Per-Facility mode.
          Local to this page: picking here re-prices the cards below, it does not switch the app's
          global facility (that's the Sidebar switcher's job). DarkSelect per CLAUDE_RULES.md. */}
      {view.showFacilityPicker && (
        <div className="card billing-card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 20px', flexWrap: 'wrap' }}>
          <Building2 size={18} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Pricing for facility</span>
          <div style={{ minWidth: 240 }}>
            <DarkSelect
              value={scopedFacility.id != null ? String(scopedFacility.id) : ''}
              onChange={(v) => { if (v) setPickedFacilityId(Number(v)); }}
              options={orgFacilities.map(f => ({ value: String(f.id), label: f.name }))}
              placeholder=""
            />
          </div>
        </div>
      )}

      {/* Org snapshot — real counts. First two cards flip to the scoped facility's own numbers
          under the 'scoped' view -- showing "17 facilities" / "50 users" right above a total that's
          actually just one facility's slice reads as a bug in itself, so they match instead. */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="card billing-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', flex: '1 1 200px' }}>
          <Building2 size={18} color="var(--text-muted)" />
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{isScoped ? (view.showFacilityPicker ? 'Selected facility' : 'Your facility') : 'Facilities in your org'}</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              {isScoped ? (scopedFacility.name || '1 facility') : facilityCount}
            </div>
          </div>
        </div>
        <div className="card billing-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', flex: '1 1 200px' }}>
          <UsersIcon size={18} color="var(--text-muted)" />
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{isScoped ? 'Users at this facility' : 'Users across your org'}</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>{billableUserCount}</div>
          </div>
        </div>
        {/* Was a standalone "Volume −X%" chip with no context when 0% -- confusing on its own.
            Spelled out with its bracket instead; the per-card chips below (under each tier's
            price) are the actual "where does the discount show" answer. Always describes the
            ORG's bracket (facilityCount, never billableFacilityCount) even when the two cards
            above have flipped to this one facility -- see the volumePct judgment-call comment
            above tierCost -- with an explicit "org-wide" callout here so it doesn't read as if a
            single facility earned this rate on its own. */}
        <div className="card billing-card" style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', flex: '1 1 200px' }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            {remote.activeDiscountCode
              ? <>Discount code active: <strong style={{ color: 'var(--text-primary)' }}>{remote.activeDiscountCode.code}</strong> (−{remote.activeDiscountCode.discountPercent}%)</>
              : volumePct > 0
                ? <>Volume discount: <strong style={{ color: 'var(--text-primary)' }}>−{volumePct}%</strong> at {facilityCount >= 21 ? '21+' : '11–20'} facilities org-wide{isScoped ? ' (applies to this facility too)' : ''}</>
                : <>Volume discount: <strong style={{ color: 'var(--text-primary)' }}>0%</strong> at 1–10 facilities</>}
          </div>
        </div>
      </div>

      {/* Monthly / Yearly cycle toggle — shared SlidingToggle component (also used by Settings'
          Billing Mode toggle) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <SlidingToggle
          value={cfg.billingCycle}
          onChange={cfg.setBillingCycle}
          options={[{ id: 'monthly', label: 'Monthly' }, { id: 'yearly', label: 'Yearly' }]}
        />
        {cfg.billingCycle === 'yearly' && remote.annualDiscount > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: '0.8125rem', fontWeight: 700, padding: '6px 14px', borderRadius: 999,
            background: 'var(--accent-success)', color: '#fff',
          }}>
            Save {remote.annualDiscount}% billed yearly
          </span>
        )}
        <DiscountChip label="Volume" pct={volumePct} tone="blue" />
        <DiscountChip label="Annual" pct={annualPct} tone="green" />
      </div>

      {/* Tier comparison cards — always visible, both billing modes; selecting one sets the
          single org-wide tier (no per-facility assignment — see header comment). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 24 }}>
        {TIER_ORDER.map((tierId) => {
          const cost = tierCost(tierId);
          const isSelected = cfg.selectedTier === tierId;
          const accent = TIER_ACCENT[tierId];
          const selectColor = TIER_SELECT_COLOR[tierId];
          // Pro's permanent "Most Popular" identity -- a soft glow around the WHOLE card (all four
          // sides), independent of selection, per the reference screenshot. Previously this was a
          // separate absolutely-positioned <div> sitting above the card (top:-6, its own
          // border-radius) -- that div's own rounded top corners read as a second, floating,
          // higher rounded rectangle whenever the card was also selected and drew its own ring via
          // box-shadow, i.e. two visually distinct rounded-box outlines stacked (the floating band's
          // curve + the card's own selection ring), which is what showed up as a double border.
          // Fix: no second element at all. Everything below is `border` + `boxShadow` on the SAME
          // .billing-tier-card div the card already is -- box-shadow can stack multiple shadows on
          // one box (comma-separated), so the permanent glow and the selection ring coexist as two
          // entries in one boxShadow value instead of two separate bordered boxes.
          const isFeatured = tierId === 'pro';
          // Always exactly 3 comma-separated layers -- ring / permanent-glow / bloom -- with a
          // zero-blur transparent placeholder standing in for whichever ones don't apply to this
          // tier/selection combo. Fixed layer count (rather than only pushing the layers that
          // apply) so this resting value's shape is identical to billingCardGlow's own 0%/100%
          // keyframe below: same 3 layers, same order, only the middle (permanent-glow) one ever
          // differing in value between the two. That's what makes the animation start/end at
          // EXACTLY the resting box-shadow with no discontinuity -- a variable layer count was
          // the reason Pro specifically would have kept snapping even after the earlier
          // billingCardPulse -> billingCardGlow fix (that fix only accounted for a single ring
          // layer, not Pro's extra permanent-glow layer).
          const ringLayer = isSelected ? `0 0 0 1px ${selectColor}` : '0 0 0 0 transparent';
          const permanentGlowLayer = isFeatured
            ? (isSelected ? '0 0 18px 3px rgba(245,158,11,0.45)' : '0 0 16px 2px rgba(245,158,11,0.35)')
            : '0 0 0 0 transparent';
          const boxShadow = `${ringLayer}, ${permanentGlowLayer}, 0 0 0 0 transparent`;
          return (
            <div
              key={tierId}
              className={`card billing-card billing-tier-card${canManage ? '' : ' billing-tier-card-readonly'}${isSelected ? ' billing-tier-card-selected' : ''}`}
              onClick={() => canManage && cfg.setSelectedTier(tierId)}
              style={{
                position: 'relative',
                padding: 20,
                // ONE border box, one border-radius (inherited from .card) -- this is the only
                // element on the whole card that draws a border. Pro's border is always orange-
                // tinted (its permanent identity, independent of selection); solid once selected.
                // Standard/Enterprise: plain subtle border until selected, then that tier's own
                // selectColor -- was hardcoded to the app's blue for all three tiers before.
                border: isFeatured
                  ? `1px solid ${isSelected ? selectColor : 'rgba(245,158,11,0.55)'}`
                  : `1px solid ${isSelected ? selectColor : 'var(--border-subtle)'}`,
                // Selected background tint now per-tier (was a hardcoded blue for every tier) --
                // same selectColor as the border/ring above, so a purple-ringed Enterprise card
                // isn't sitting on a leftover blue tint. `1A` suffix = ~10% alpha, matching the
                // 0.10 opacity the mockup's blue selected state already used.
                background: isSelected ? `${selectColor}1A` : undefined,
                boxShadow,
                display: 'flex', flexDirection: 'column', gap: 14,
                // Feeds billingCardGlow below (the selection-pulse animation) so it blooms in the
                // right color per tier instead of being hardcoded blue -- see Billing.css.
                ['--tier-glow-color' as string]: selectColor,
                ['--tier-glow-color-soft' as string]: TIER_SELECT_GLOW[tierId],
                ['--tier-permanent-glow' as string]: permanentGlowLayer,
              } as CSSProperties}
            >
              {isFeatured && (
                // Compact corner-tag badge straddling the card's own top border line (not a
                // full-width bar) -- positioned via absolute positioning OVER the card, drawn on
                // top of the one bordered box above, not as a second bordered element of its own.
                // translateY(-50%) centers it exactly on the border line so it visually "sits on"
                // the card's own top edge rather than floating above it or falling inside it.
                <div style={{
                  position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: '#f59e0b', color: '#fff',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', whiteSpace: 'nowrap',
                  padding: '5px 14px', borderRadius: 999,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)', zIndex: 3,
                }}>
                  <span aria-hidden="true">★</span> MOST POPULAR
                </div>
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Real pill badge now (was plain colored text) -- matches mockup's .tier-badge:
                      font-size 9px, padding 2px 8px, radius 99px, background at 15% of the tier's
                      own accent. Same TIER_ACCENT hex the badge/band already use elsewhere, just
                      applied here too instead of leaving this one spot as unstyled text. */}
                  <span style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                    padding: '2px 8px', borderRadius: 999,
                    color: accent, background: `${accent}26`,
                  }}>
                    {TIER_LABEL[tierId]}
                  </span>
                  {/* Radio circle — empty ring when unselected, fills blue with a checkmark when
                      this is the current plan. This is the actual selection indicator now. */}
                  {/* Fill color now this tier's own selectColor too (was always the blue accent,
                      regardless of tier) -- otherwise a purple-ringed Enterprise card would still
                      show a blue checkmark circle, right back to the same per-tier inconsistency
                      just fixed on the card's border/background above. */}
                  <span style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `2px solid ${isSelected ? selectColor : 'var(--border-default)'}`,
                    background: isSelected ? selectColor : 'transparent',
                    transition: 'background 150ms ease, border-color 150ms ease',
                  }}>
                    {isSelected && <Check size={11} color="#fff" strokeWidth={3} />}
                  </span>
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <AnimatedPrice value={cost.totalMonthly} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ month{cfg.billingCycle === 'yearly' ? ', billed yearly' : ''}</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {isScoped
                    ? `${formatMoney(cost.perFacilityRate)} / mo for ${scopedFacility.name || 'this facility'}`
                    : `${formatMoney(cost.perFacilityRate)} / facility/mo × ${billableFacilityCount} facilit${billableFacilityCount === 1 ? 'y' : 'ies'}`}
                  {cost.seatOverage > 0 && ` + ${formatMoney(cost.seatOverage)} seat overage`}
                </div>
                {/* Per-card discount chips -- the same volume/annual % applies to every tier
                    (org-wide brackets), shown here rather than only in the ambiguous top badge. */}
                {(volumePct > 0 || annualPct > 0) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <DiscountChip label="Volume" pct={volumePct} tone="blue" />
                    <DiscountChip label="Annual" pct={annualPct} tone="green" />
                  </div>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
                {TIER_FEATURES[tierId].map((f) => (
                  <li key={f} style={{ display: 'flex', gap: 8, fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    <Check size={14} color={accent} style={{ flexShrink: 0, marginTop: 2 }} />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`btn btn-sm${isSelected ? ' btn-ghost' : ' btn-primary'}`}
                disabled={!canManage || isSelected}
                onClick={(e) => { e.stopPropagation(); cfg.setSelectedTier(tierId); }}
                style={{
                  alignSelf: 'center', // card is flex-column, default stretch would make this full-width
                  opacity: !canManage && !isSelected ? 0.5 : 1,
                  cursor: !canManage ? 'not-allowed' : 'pointer',
                }}
                title={!canManage ? 'You do not have permission to change the plan' : undefined}
              >
                {isSelected ? 'Current Plan' : 'Select This Plan'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Discount code redemption -- moved above Cost breakdown/Checkout (was below) so a code can
          be entered and applied BEFORE checkout, not after. Once used by any org (this one
          included), permanently spent; the server enforces the same billing-mode "who manages this
          org's plan" gate as the Select This Plan buttons above, not just this client-side canManage
          check. Inside the same dataReady / non-restricted gate as everything else: it used to sit
          outside it and read canManage before the billing mode had loaded, and a restricted role
          must not see it. handleRedeem's qc.invalidateQueries(['billing-effective']) refetches
          `remote` on success -- every cost figure below (tierCost/volumePct/annualPct/chargeCost)
          is derived fresh from `remote` on every render, not cached, so the breakdown and the
          checkout confirmation line both pick up the new discount immediately, no extra plumbing
          needed here. */}
      <div className="card billing-card" style={{ padding: 20, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag size={16} /> Have a discount code?
        </h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '0 0 12px' }}>
          Replaces your org's volume/annual discount while active — not stacked on top of it.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            placeholder="Enter code"
            value={codeInput}
            disabled={!canManage}
            onChange={(e) => setCodeInput(e.target.value)}
            style={{ maxWidth: 220, textTransform: 'uppercase' }}
          />
          <button
            className="btn btn-primary btn-sm"
            disabled={!canManage || !codeInput.trim() || redeeming}
            onClick={handleRedeem}
          >
            {redeeming ? 'Applying…' : 'Apply'}
          </button>
        </div>
        {!canManage && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '8px 0 0' }}>
            Only whoever manages this org's plan (see Billing Mode above) can redeem a code.
          </p>
        )}
      </div>

      {/* Cost breakdown for the currently selected tier */}
      <div className="card billing-card" style={{ padding: 20, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px' }}>Cost breakdown — {TIER_LABEL[cfg.selectedTier]}</h3>
        {isScoped && (
          <p style={{ margin: '0 0 14px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            Scoped to {scopedFacility.name || 'this facility'} — 1 of {facilityCount} facilities in the org, not the org-wide total.
          </p>
        )}
        {!isScoped && <div style={{ marginBottom: 10 }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.875rem' }}>
          <Row label={`Base rate (${formatMoney(remote.tiers[cfg.selectedTier].basePricePerFacilityMonth)}/facility/mo)`} value={formatMoney(remote.tiers[cfg.selectedTier].basePricePerFacilityMonth * billableFacilityCount)} />
          {/* Dollar amount scales off billableFacilityCount (this facility's own subtotal) but the
              label's own facility count stays org-wide (facilityCount) -- it's describing WHY this
              rate applies (the org's 16/17-facility bracket), not how many facilities are being
              billed here. See the volumePct judgment-call comment above tierCost. */}
          {volumePct > 0 && <Row label={`Volume discount (−${volumePct}%, ${facilityCount} facilities org-wide)`} value={`−${formatMoney(remote.tiers[cfg.selectedTier].basePricePerFacilityMonth * billableFacilityCount * (volumePct / 100))}`} muted />}
          {annualPct > 0 && <Row label={`Annual discount (−${annualPct}%)`} value={`−${formatMoney(remote.tiers[cfg.selectedTier].basePricePerFacilityMonth * billableFacilityCount * (1 - volumePct / 100) * (annualPct / 100))}`} muted />}
          <Row label={`Seats included (${selectedCost.includedSeats} across ${billableFacilityCount} facilit${billableFacilityCount === 1 ? 'y' : 'ies'})`} value={`${billableUserCount} in use`} muted />
          {selectedCost.extraSeats > 0 && (
            <Row label={`Extra seats (${selectedCost.extraSeats} × ${formatMoney(remote.tiers[cfg.selectedTier].pricePerExtraSeatMonth)})`} value={formatMoney(selectedCost.seatOverage)} />
          )}
          <div style={{ borderTop: '1px solid var(--border-default)', margin: '4px 0' }} />
          <Row label={cfg.billingCycle === 'yearly' ? 'Total per month (billed yearly)' : 'Total per month'} value={formatMoney(selectedCost.totalMonthly)} bold />
          {cfg.billingCycle === 'yearly' && (
            <Row label="Total per year" value={formatMoney(selectedCost.totalMonthly * 12)} muted />
          )}
        </div>

        {/* Checkout -- the ONLY thing on this page that starts Stripe Checkout. The confirmation
            line states exactly what create-checkout-session will charge (chargeCost): ONE facility
            under Per-Facility mode, every facility in the org under Centralized mode. */}
        <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 16, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          {planLocked ? (
            <>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{subscriptionSummary}</p>
              {/* Real cancel action (this ticket) instead of the old "manage from your billing
                  portal" dead end -- the Customer Portal itself is still Phase 4/separate. Targets
                  whichever subscription this card is currently showing: the scoped facility's own
                  under Per-Facility mode, the org-wide one under Centralized. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: '#ef4444' }}
                  onClick={() => setCancelTarget(isScoped && scopedFacility.id != null
                    ? { facilityId: scopedFacility.id, label: `${scopedFacility.name || 'this facility'}'s subscription` }
                    : { label: 'this subscription' })}
                >
                  Cancel Subscription
                </button>
                {/* Admin-only, deliberately a small secondary text link -- never the same weight or
                    click as the routine Cancel button above, never shown to org_admin/manager. */}
                {user?.role === 'admin' && (
                  <button
                    type="button"
                    onClick={() => void openRefundFlow(isScoped && scopedFacility.id != null
                      ? { facilityId: scopedFacility.id, label: `${scopedFacility.name || 'this facility'}'s subscription` }
                      : { label: 'this subscription' })}
                    style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.75rem', color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Issue a real refund instead (admin)
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Read-only viewer (org_admin under Per-Facility mode) looking at a subscribed facility */}
              {subscriptionSummary && (
                <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{subscriptionSummary}</p>
              )}
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {cfg.billingCycle === 'yearly'
                  ? <>You&rsquo;ll be charged <strong style={{ color: 'var(--text-primary)' }}>{formatMoneyExact(chargeCost.totalMonthly * 12)}/year</strong> (billed once, upfront) for {isScoped ? <>{scopedFacility.name || 'this facility'}</> : <>all {facilityCount} facilit{facilityCount === 1 ? 'y' : 'ies'} in your organization</>}.</>
                  : <>You&rsquo;ll be charged <strong style={{ color: 'var(--text-primary)' }}>{formatMoneyExact(chargeCost.totalMonthly)}/month</strong>, recurring until canceled, for {isScoped ? <>{scopedFacility.name || 'this facility'}</> : <>all {facilityCount} facilit{facilityCount === 1 ? 'y' : 'ies'} in your organization</>}.</>}
              </p>
              <button
                className="btn btn-primary btn-sm"
                disabled={!canManage || startingCheckout}
                onClick={() => { void handleCheckout(); }}
                title={!canManage ? 'You do not have permission to change the plan' : undefined}
              >
                {startingCheckout ? 'Starting checkout…' : 'Proceed to Checkout'}
              </button>
            </>
          )}
        </div>
      </div>
      </>
      )}
      {cancelTarget && (
        <CancelSubscriptionModal
          label={cancelTarget.label}
          canceling={canceling}
          onConfirm={handleCancelConfirm}
          onClose={() => { if (!canceling) setCancelTarget(null); }}
        />
      )}
      {refundTarget && (
        <RefundConfirmModal
          label={refundTarget.label}
          preview={refundPreview}
          loading={loadingRefundPreview}
          refunding={refunding}
          onConfirm={handleRefundConfirm}
          onClose={() => { if (!refunding) setRefundTarget(null); }}
        />
      )}
    </div>
  );
}

function Row({ label, value, muted, bold }: { label: string; value: string; muted?: boolean; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ color: muted ? 'var(--text-muted)' : 'var(--text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: bold ? 700 : 500, fontSize: bold ? '1rem' : undefined }}>{value}</span>
    </div>
  );
}
